/**
 * Cookie Preferences Modal
 *
 * Dialog for granular cookie consent management.
 * Allows users to toggle individual cookie categories.
 */

import { useTranslation } from 'react-i18next'
import { Cookie, Shield, BarChart3, Megaphone } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Switch,
  Label,
  Separator,
} from '@tonesmith/ui'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'
import { analytics } from '@/lib/analytics'

interface CookiePreferencesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CookiePreferencesModal({ open, onOpenChange }: CookiePreferencesModalProps) {
  const { t } = useTranslation('common')
  const { consent, updateConsent, hasConsented } = useCookieConsentStore()

  const handleAnalyticsToggle = (checked: boolean) => {
    updateConsent({ analytics: checked })

    if (checked) {
      analytics.onConsentGranted()
    } else {
      analytics.onConsentRevoked()
    }
  }

  const handleMarketingToggle = (checked: boolean) => {
    updateConsent({ marketing: checked })
    // Marketing consent doesn't affect analytics currently
  }

  const handleSave = () => {
    // If this is the first time consenting, trigger analytics if enabled
    if (!hasConsented && consent.analytics) {
      analytics.onConsentGranted()
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5" />
            {t('cookies.preferences.title')}
          </DialogTitle>
          <DialogDescription>{t('cookies.preferences.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Necessary Cookies */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <div className="flex gap-3">
              <Shield className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <Label className="font-medium">{t('cookies.categories.necessary.title')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.categories.necessary.description')}
                </p>
              </div>
            </div>
            <Switch checked={true} disabled aria-label={t('cookies.categories.necessary.title')} />
          </div>

          <Separator />

          {/* Analytics Cookies */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <div className="flex gap-3">
              <BarChart3 className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <Label htmlFor="analytics-switch" className="font-medium">
                  {t('cookies.categories.analytics.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.categories.analytics.description')}
                </p>
              </div>
            </div>
            <Switch
              id="analytics-switch"
              checked={consent.analytics}
              onCheckedChange={handleAnalyticsToggle}
              aria-label={t('cookies.categories.analytics.title')}
            />
          </div>

          {/* Marketing Cookies */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <div className="flex gap-3">
              <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <Label htmlFor="marketing-switch" className="font-medium">
                  {t('cookies.categories.marketing.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.categories.marketing.description')}
                </p>
              </div>
            </div>
            <Switch
              id="marketing-switch"
              checked={consent.marketing}
              onCheckedChange={handleMarketingToggle}
              aria-label={t('cookies.categories.marketing.title')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave}>{t('cookies.preferences.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
